import { IconButton } from '@/components/ui/IconButton';
import { cn } from '@/lib/cn';
import type { LucideIcon } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';

type Props = {
  icon: LucideIcon;
  label: string;
  children: ReactNode;
  className?: string;
};

export function SidebarCompactPopover({ icon: Icon, label, children, className }: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={cn('relative flex justify-center', className)}>
      <IconButton
        variant="ghost"
        className="text-[var(--fv-sidebar-text)] hover:bg-white/10 hover:text-white"
        aria-label={label}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon className="h-4 w-4" aria-hidden />
      </IconButton>
      {open && (
        <div
          className="absolute start-full top-0 z-50 ms-2 w-56 rounded-lg border border-[var(--fv-border)] bg-[var(--fv-surface)] p-3 shadow-[var(--fv-shadow-md)]"
          role="dialog"
          aria-label={label}
        >
          {children}
        </div>
      )}
    </div>
  );
}
